// Self-contained Earnings Calendar widget: owns the US/DE market toggle, the
// TanStack Query fetch, and the loading / error / empty states. Drop this into
// any page (e.g. BreakoutInsightsPage) with a bare <EarningsWidget />.

import { useState } from 'react';

import { Activity } from 'lucide-react';

import { useEarningsCalendar, type MarketType } from '../api/useEarningsCalendar';
import { EarningsTable } from './EarningsTable';

export const EarningsWidget = () => {
    const [market, setMarket] = useState<MarketType>('US');
    const { data, isLoading, isError } = useEarningsCalendar(market);

    return (
        <div className="flex flex-col space-y-4">
            {/* Header + controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                        <Activity className="w-5 h-5 text-indigo-500" />
                        Week Ahead: Key Earnings
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Largest-capitalization companies reporting over the next
                        7 days.
                    </p>
                </div>

                {/* Market toggle */}
                <div className="flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                    <button
                        type="button"
                        onClick={() => setMarket('US')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                            market === 'US'
                                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                        US Equities
                    </button>
                    <button
                        type="button"
                        onClick={() => setMarket('DE')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                            market === 'DE'
                                ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
                                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                        }`}
                    >
                        European (DAX)
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="min-h-[400px]">
                {isLoading ? (
                    <div className="flex h-[400px] items-center justify-center rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                        <div className="animate-pulse font-medium text-slate-400">
                            Loading calendar data…
                        </div>
                    </div>
                ) : isError ? (
                    <div className="rounded-xl border border-red-100 bg-red-50 p-4 text-red-600 dark:border-red-900 dark:bg-red-900/20 dark:text-red-400">
                        Failed to load earnings calendar. Please try again later.
                    </div>
                ) : data ? (
                    <EarningsTable days={data.days} market={market} />
                ) : null}
            </div>
        </div>
    );
};
